import React, { useRef, useEffect, useMemo, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { integration, i18next, site, template } from '@yola/ws-sdk';
import segment from 'src/js/modules/analytics/segment';
import getHelpMenuItemClickCallbacks from 'yola-editor/src/js/modules/editor/settings/get-help-menu-item-click-callbacks';
import useMatchBreakpoint from 'src/js/modules/utils/custom-hooks/use-match-breakpoint';
import MainMenu from '../../common/components/main-menu';
import getSettingByName from '../helpers/get-setting-by-name';
import sortMenuItems from '../helpers/sort-menu-items';
import itemIds from '../constants/item-ids';
import iconTypes from '../../common/constants/menu-item-icon-types';

const { UP, DOWN } = iconTypes;

const {
  trackers: { trackEvent },
  constants: { events, triggerIds },
} = segment;

const DESKTOP_VIEW_BREAKPOINT = '768px';

function HelpMenuContainer() {
  const isDesktopView = useMatchBreakpoint(DESKTOP_VIEW_BREAKPOINT);

  const [isOpen, setIsOpen] = useState(false);
  const iconGlyph = isOpen ? UP : DOWN;
  const settings = useSelector(integration.selectors.getSettings);

  const rawHelpMenuItems = getSettingByName(settings, 'helpMenu.submenu', []);
  const helpMenuTitle = getSettingByName(settings, 'helpMenu.heading', '');
  const helpMenuHref = getSettingByName(settings, 'helpMenu.href', '');
  const helpMenuID = getSettingByName(settings, 'helpMenu.id', '');

  const menuElement = useRef(null);

  const isMultilingualSite = site.verifiers.isMultilingualSite();
  const helpMenuItems = useMemo(() => {
    if (isMultilingualSite) {
      const { multilingualTourOnClick } = getHelpMenuItemClickCallbacks(triggerIds.EDITOR_HEADER);

      const multilingualTourMenuItem = {
        title: i18next.t('Multilingual tour'),
        heading: i18next.t('Multilingual tour'),
        id: itemIds.MULTILINGUAL_TOUR,
        description: '',
        order: 4,
        onClick: multilingualTourOnClick,
      };

      return [...rawHelpMenuItems, multilingualTourMenuItem, multilingualTourOnClick];
    }

    return rawHelpMenuItems;
  }, [isMultilingualSite, rawHelpMenuItems]);

  useEffect(() => {
    menuElement.current = document.getElementById(helpMenuID);
  }, [helpMenuID]);

  const helpMenuOnShowHandler = useCallback(() => {
    setIsOpen(true);

    trackEvent(events.APP_HELP_MENU_DISPLAYED);
  }, []);

  const helpMenuOnHideHandler = () => {
    setIsOpen(false);
  };

  const sortedMenuItems = useMemo(() => sortMenuItems(helpMenuItems), [helpMenuItems]);

  const captions = useMemo(() => ({ name: helpMenuTitle }), [helpMenuTitle]);

  if (!helpMenuHref && helpMenuItems.length === 0) {
    return null;
  }

  return (
    <MainMenu
      href={helpMenuHref}
      onShow={helpMenuOnShowHandler}
      onHide={helpMenuOnHideHandler}
      captions={captions}
      className="ws-mainmenu--help-menu"
      items={sortedMenuItems}
      showDetails={false}
      iconGlyph={iconGlyph}
      isHidden={!isDesktopView}
    />
  );
}

export default HelpMenuContainer;
